/**
 * Shared Captions v2 primitives — status pills, language chips,
 * source-provider tags, and reviewer-flag chips from the prototype's
 * caption shared section (cms-redesign-mockup.html ~3537+, status
 * tokens 3546–3580).
 *
 * Imported by all four Captions list pages (Review Queue, Generate,
 * Vocabulary, Translation) AND the per-session Editor — so the
 * workflow vocabulary reads consistently across every caption surface.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
// Import `cn` from its own module rather than the redesign barrel —
// the barrel pulls in TopBar → RouteConfig → the entire route graph,
// which makes this module expensive to import in places that only
// need the small helpers (Jest tests, light surfaces).
import { cn } from '../../redesign/cn';
import { SEVERITY_COLOR } from '../../../constants/captionStyle';

/* ─── Caption status pill ──────────────────────────────────────────── */
/*
 *  Six workflow states surface in the queue + per-session panel. The
 *  pill = uppercase Asap Condensed 10/700 with a 6px tinted dot
 *  prefix. Colors match the prototype's `.cap-status.st-*` classes.
 */
const STATUS_STYLES = {
  queued:                { bg: '#E8E0D5', color: '#6B6470' },
  processing:            { bg: '#DCE7F5', color: '#3A4F7A' },
  auto_pending_review:   { bg: '#FCEAD1', color: '#8C5A1E' },
  pending:               { bg: '#FCEAD1', color: '#8C5A1E' }, // alias
  human_reviewed:        { bg: '#D6EBE3', color: '#5C7E72' },
  reviewed:              { bg: '#D6EBE3', color: '#5C7E72' }, // alias
  human_edited:          { bg: '#D6EBE3', color: '#5C7E72' },
  edited:                { bg: '#D6EBE3', color: '#5C7E72' }, // alias
  failed:                { bg: '#E58A6A26', color: '#C4715B' },
};

const STATUS_LABELS = {
  queued:              'Queued',
  processing:          'Processing',
  auto_pending_review: 'Pending review',
  pending:             'Pending review',
  human_reviewed:      'Reviewed',
  reviewed:            'Reviewed',
  human_edited:        'Edited',
  edited:              'Edited',
  failed:              'Failed',
};

export function CapStatus({ status, flagCount, label, className }) {
  const key = (status || '').toLowerCase();
  const style = STATUS_STYLES[key] || STATUS_STYLES.queued;
  const text = label || STATUS_LABELS[key] || status || 'Unknown';
  return (
    <span
      className={cn(
        'inline-flex items-center font-condensed font-bold uppercase whitespace-nowrap',
        className,
      )}
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        padding: '3px 9px',
        borderRadius: 999,
        gap: 4,
        background: style.bg,
        color: style.color,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'currentColor',
          opacity: 0.85,
        }}
      />
      {text}
      {flagCount > 0 && (
        <span
          style={{
            background: '#00000018',
            color: 'inherit',
            padding: '0 5px',
            borderRadius: 999,
            marginLeft: 4,
            fontSize: 9.5,
          }}
        >
          {flagCount}
        </span>
      )}
    </span>
  );
}

/* ─── Language chip ────────────────────────────────────────────────── */
/*
 *  Compact mono pill ("EN", "ES-MX"). When `status` is supplied the
 *  chip tints itself to match (pending = orange, reviewed = teal,
 *  processing = blue, failed = coral). Default is the neutral
 *  cream/sand chip.
 */
const CHIP_STATUS_STYLES = {
  pending:              { bg: '#FCEAD1', border: '#E8C796', color: '#8C5A1E' },
  auto_pending_review:  { bg: '#FCEAD1', border: '#E8C796', color: '#8C5A1E' },
  reviewed:             { bg: '#D6EBE3', border: '#A7C7BB', color: '#5C7E72' },
  human_reviewed:       { bg: '#D6EBE3', border: '#A7C7BB', color: '#5C7E72' },
  human_edited:         { bg: '#D6EBE3', border: '#A7C7BB', color: '#5C7E72' },
  processing:           { bg: '#DCE7F5', border: '#B5C6E1', color: '#3A4F7A' },
  failed:               { bg: '#E58A6A26', border: '#E58A6A', color: '#C4715B' },
};

export function LangChip({ code, status, className }) {
  const styled = status
    ? CHIP_STATUS_STYLES[status.toLowerCase()]
    : undefined;
  return (
    <span
      className={cn('inline-block font-mono uppercase', className)}
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 4,
        background: styled?.bg || '#FAF7F1',
        border: `1px solid ${styled?.border || 'rgb(var(--color-sand))'}`,
        color: styled?.color || '#2C3E50',
      }}
    >
      {(code || '??').toUpperCase()}
    </span>
  );
}

/* ─── Source provider tag (assemblyai / whisper / manual / translated) ── */

const SOURCE_LABELS = {
  assemblyai: 'AssemblyAI',
  whisper:    'Whisper',
  manual:     'Manual',
  translated: 'Translated',
};

export function SourceTag({ source, className }) {
  if (!source) return null;
  const label = SOURCE_LABELS[String(source).toLowerCase()] || source;
  return (
    <span
      className={cn(
        'inline-block font-mono text-text-muted bg-cream border border-sand',
        className,
      )}
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </span>
  );
}

/* ─── Reviewer-flag chip ───────────────────────────────────────────── */
/*
 *  Per-flag chip used by the Review Queue table and the per-session
 *  Editor's "Reviewer flags" sidebar. Severity → tinted background +
 *  matching border + readable text. Falls back to v1's MUI palette
 *  name mapping (via SEVERITY_COLOR) if a new severity ships before
 *  this map knows about it.
 */
const FLAG_TINT = {
  high:    { bg: '#E58A6A26', border: '#E58A6A', color: '#C4715B' },
  medium:  { bg: '#FCEAD1',   border: '#E8C796', color: '#8C5A1E' },
  low:     { bg: '#DCE7F5',   border: '#B5C6E1', color: '#3A4F7A' },
  info:    { bg: '#FAF7F1',   border: 'rgb(var(--color-sand))', color: '#6B6470' },
};

export function flagTint(severity) {
  const sev = (severity || '').toLowerCase();
  if (FLAG_TINT[sev]) return FLAG_TINT[sev];
  const mui = SEVERITY_COLOR?.[severity];
  if (mui === 'error')   return FLAG_TINT.high;
  if (mui === 'warning') return FLAG_TINT.medium;
  if (mui === 'info')    return FLAG_TINT.low;
  return FLAG_TINT.info;
}

export function FlagChip({ flag, className }) {
  const tint = flagTint(flag.severity);
  return (
    <span
      className={cn(
        'inline-flex items-center font-condensed font-semibold uppercase whitespace-nowrap',
        className,
      )}
      style={{
        fontSize: 10,
        letterSpacing: '0.06em',
        padding: '2px 7px',
        borderRadius: 999,
        background: tint.bg,
        border: `1px solid ${tint.border}`,
        color: tint.color,
      }}
      title={flag.flag_type}
    >
      {String(flag.flag_type || 'flag').replace(/_/g, ' ')}
    </span>
  );
}

/* ─── CaptionWaveform — slim wavesurfer.js v7 player ───────────────── */
/*
 *  Brand-skinned waveform + circular play/pause + mm:ss/mm:ss readout.
 *  Used by the CaptionsEditor page to give the operator a visual sense
 *  of position + silence patterns while editing cue text.
 *
 *  Why a separate primitive from `redesign/edit/WaveformPlayer`? That
 *  one carries session-editor-specific props (contentStart, ratingStart,
 *  tappingPoints) and an upload affordance. The captions surface has
 *  read-only audio (the session's MP3 is already attached upstream) and
 *  doesn't need any markers — operator navigation happens via the cue
 *  list, not the waveform. This keeps the API tiny and intentional.
 *
 *  Usage:
 *    const waveRef = useRef(null);
 *    <CaptionWaveform
 *      ref={waveRef}
 *      url={session.session_main_url}
 *      onTimeUpdate={(sec) => updateActiveCue(sec)}
 *      onReady={(durationSec) => setDuration(durationSec)}
 *    />
 *    // To seek from a cue click:
 *    waveRef.current?.seek(cue.start);
 *
 *  Imperative ref handle exposes `seek(sec)` and `play()` so the cue
 *  list can drive playback without prop-drilling state.
 */

function fmtMMSS(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return (
    String(Math.floor(s / 60)).padStart(2, '0') +
    ':' +
    String(s % 60).padStart(2, '0')
  );
}

const PLAY_ICON_SMALL = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="6 4 20 12 6 20 6 4" />
  </svg>
);
const PAUSE_ICON_SMALL = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

export const CaptionWaveform = forwardRef(function CaptionWaveform(
  { url, onTimeUpdate, onReady, className },
  ref,
) {
  const containerRef = useRef(null);
  const wsRef        = useRef(null);
  // Latest-callback refs so we can re-instantiate wavesurfer only on
  // `url` change. Callers can pass fresh closures every render without
  // forcing the canvas to rebuild.
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onReadyRef      = useRef(onReady);
  onTimeUpdateRef.current = onTimeUpdate;
  onReadyRef.current      = onReady;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  /* ─── Imperative handle exposed to the parent. ──────────────── */
  useImperativeHandle(ref, () => ({
    seek(sec) {
      const ws = wsRef.current;
      if (!ws || duration <= 0) return;
      const clamped = Math.max(0, Math.min(duration, sec));
      ws.setTime(clamped);
      setCurrentTime(clamped);
      if (ws.isPlaying && !ws.isPlaying()) ws.play();
    },
    play() {
      wsRef.current?.play();
    },
    pause() {
      wsRef.current?.pause();
    },
    isReady() {
      return ready;
    },
  }), [duration, ready]);

  /* ─── Build wavesurfer per URL. Same teardown recipe as
   *     WaveformPlayer to avoid the dreaded "destroyed instance still
   *     in callbacks" warning. ──────────────────────────────────── */
  useEffect(() => {
    if (!url || !containerRef.current) return undefined;

    setReady(false);
    setLoadError(null);
    setCurrentTime(0);
    setDuration(0);

    let cancelled = false;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      url,
      waveColor:    '#DDD3BE', // lighter sand
      progressColor:'#75A293', // teal-green
      cursorColor:  '#5C7E72', // teal-green-d
      barWidth: 2,
      barGap: 1,
      barRadius: 1.5,
      height: 64,
      normalize: true,
      mediaControls: false,
      interact: true,
    });
    wsRef.current = ws;

    const onReadyEvt = () => {
      if (cancelled) return;
      setReady(true);
      const d = ws.getDuration();
      setDuration(d);
      onReadyRef.current?.(d);
    };
    const onAudioProcess = () => {
      if (cancelled) return;
      const t = ws.getCurrentTime();
      setCurrentTime(t);
      onTimeUpdateRef.current?.(t);
    };
    const onPlay   = () => setIsPlaying(true);
    const onPause  = () => setIsPlaying(false);
    const onFinish = () => {
      setIsPlaying(false);
      setCurrentTime(ws.getDuration());
    };
    const onErr = (err) => {
      // Wavesurfer surfaces decode errors here; show a quiet badge
      // rather than crash the page.
      console.warn('CaptionWaveform:', err);
      setLoadError('Waveform unavailable — playback may not work.');
    };

    ws.on('ready',        onReadyEvt);
    ws.on('audioprocess', onAudioProcess);
    ws.on('seeking',      onAudioProcess);
    ws.on('play',         onPlay);
    ws.on('pause',        onPause);
    ws.on('finish',       onFinish);
    ws.on('error',        onErr);

    return () => {
      cancelled = true;
      try { ws.destroy(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [url]);

  const handlePlayPause = () => wsRef.current?.playPause();

  if (!url) {
    return (
      <div
        className={cn('text-text-muted italic', className)}
        style={{
          background: 'rgb(var(--color-cream))',
          border: '1px solid rgb(var(--color-sand))',
          borderRadius: 8,
          padding: '14px 18px',
          fontSize: 12.5,
        }}
      >
        Loading session audio…
      </div>
    );
  }

  return (
    <div
      className={cn(className)}
      style={{
        background: 'rgb(var(--color-cream))',
        border: '1px solid rgb(var(--color-sand))',
        borderRadius: 8,
        padding: 14,
        minWidth: 0,
      }}
    >
      <div style={{ position: 'relative', minHeight: 64 }}>
        <div
          ref={containerRef}
          style={{
            width: '100%',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 1,
            minHeight: 64,
          }}
        />
        {!ready && !loadError && (
          <div
            className="text-text-muted"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              background: 'rgb(var(--color-cream) / 0.88)',
              fontSize: 12,
              zIndex: 2,
            }}
          >
            Loading waveform…
          </div>
        )}
        {loadError && (
          <div
            className="text-coral-dark"
            style={{
              position: 'absolute',
              top: 4,
              right: 6,
              fontSize: 11,
              zIndex: 2,
              background: 'rgb(var(--color-surface))',
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid rgb(var(--color-sand))',
            }}
            title={loadError}
          >
            Waveform unavailable
          </div>
        )}
      </div>

      {/* Controls row — play/pause + mm:ss/mm:ss readout. */}
      <div
        className="flex items-center"
        style={{
          gap: 12,
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid rgb(var(--color-sand))',
        }}
      >
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={!ready}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className="grid place-items-center cursor-pointer"
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: '#75A293',
            color: '#fff',
            border: 0,
            opacity: ready ? 1 : 0.5,
            boxShadow: '0 2px 6px #75A29360, inset 0 1px 0 #FFFFFF30',
            flexShrink: 0,
          }}
        >
          {isPlaying ? PAUSE_ICON_SMALL : PLAY_ICON_SMALL}
        </button>
        <span
          className="font-mono text-navy"
          style={{ fontSize: 12.5, fontWeight: 600 }}
        >
          {fmtMMSS(currentTime)} / {fmtMMSS(duration)}
        </span>
        <span
          className="text-text-muted"
          style={{ fontSize: 11.5, marginLeft: 'auto' }}
        >
          Click the waveform to scrub
        </span>
      </div>
    </div>
  );
});
